import React, {useEffect, useRef, useState} from 'react';
import {Link} from "react-router-dom";

import './student_intro_v1.scss';

import Mascot from "./Mascot";
import Confetti from "./Confetti";

const FLOATING_SYMBOLS = ['+', '×', '÷', 'π', '%', '=', '△', '√', '○', '½'];

const CHALLENGES = [
    {
        icon: '👕',
        animation: 'wiggle',
        title: 'Design T-shirts',
        text: 'Measure, scale and create T-shirts that celebrate trailblazing heroes.'
    },
    {
        icon: '🥞',
        animation: 'flip',
        title: 'Cook the perfect pancake',
        text: 'Tweak a recipe’s ingredients until you find the tastiest pancake ever.'
    },
    {
        icon: '🌍',
        animation: 'spin',
        title: 'Solve real-world puzzles',
        text: 'Use math to figure out problems people actually face in everyday life and real jobs.'
    }
];

const STEPS = [
    {
        icon: '🔑',
        title: 'Get your code',
        text: 'Your teacher shares a class code so you can jump in.'
    },
    {
        icon: '🧭',
        title: 'Dive into a challenge',
        text: 'Explore a real-world situation, one step at a time.'
    },
    {
        icon: '💡',
        title: 'Think it through',
        text: 'Try ideas, make mistakes, try again. That’s how brains grow!'
    },
    {
        icon: '📣',
        title: 'Share your thinking',
        text: 'Explain how you got there. There’s often more than one right way.'
    }
];

const SKILLS = [
    {icon: '🧩', label: 'Problem solving'},
    {icon: '🧠', label: 'Critical thinking'},
    {icon: '💬', label: 'Communication'},
    {icon: '🤝', label: 'Teamwork'},
    {icon: '📐', label: 'Math reasoning'},
    {icon: '🚀', label: 'Future careers'}
];

function useReveal(ref) {
    useEffect(() => {
        const root = ref.current;
        if (!root) {
            return;
        }
        const elements = root.querySelectorAll('[data-reveal]');
        if (typeof window.IntersectionObserver !== 'function') {
            elements.forEach(element => element.classList.add('is-visible'));
            return;
        }
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, {threshold: 0.2});
        elements.forEach(element => observer.observe(element));

        return () => observer.disconnect();
    }, []);
}

export default function () {
    const pageRef = useRef(null);
    const [celebrating, setCelebrating] = useState(0);

    useReveal(pageRef);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Welcome to Mindsets!';

        return () => {
            document.title = previousTitle;
        }
    }, []);

    function scrollToAbout() {
        const about = document.getElementById('student-intro-v1-about');
        if (about) {
            about.scrollIntoView({behavior: 'smooth'});
        }
    }

    return (
        <div id='student-intro-v1' ref={pageRef}>
            <section className='student-intro-hero'>
                <div className='student-intro-floaters' aria-hidden='true'>
                    {FLOATING_SYMBOLS.map((symbol, index) => (
                        <span key={index} className={`student-intro-floater floater-${index}`}>{symbol}</span>
                    ))}
                </div>
                <img
                    className='student-intro-logo'
                    src={`${process.env.PUBLIC_S3_ASSETS_URL}/logos/mindsets_logo_white.svg`}
                    alt='Mindsets'
                />
                <div className='student-intro-hero-content'>
                    <Mascot/>
                    <h1 className='student-intro-title'>
                        <span className='student-intro-wave' aria-hidden='true'>👋</span>
                        Hey there, welcome to Mindsets!
                    </h1>
                    <p className='student-intro-subtitle'>
                        We’re <strong>so</strong> glad you’re here. Let’s show you what it’s all about.
                    </p>
                    <button className='student-intro-scroll' onClick={scrollToAbout}>
                        Show me
                        <span className='student-intro-scroll-arrow' aria-hidden='true'>↓</span>
                    </button>
                </div>
            </section>

            <section id='student-intro-v1-about' className='student-intro-section student-intro-about'>
                <h2 data-reveal>So… what is Mindsets?</h2>
                <p className='student-intro-lead' data-reveal>
                    Mindsets turns math into <span className='student-intro-highlight'>real-life adventures</span>.
                    Instead of just answering questions on a worksheet, you’ll use math to solve problems
                    from the real world, just like people do in all kinds of cool jobs.
                </p>
                <div className='student-intro-cards'>
                    {CHALLENGES.map((challenge, index) => (
                        <div
                            key={index}
                            className='student-intro-card'
                            style={{transitionDelay: `${index * 150}ms`}}
                            data-reveal
                        >
                            <div className={`student-intro-card-icon animation-${challenge.animation}`} aria-hidden='true'>
                                {challenge.icon}
                            </div>
                            <h3>{challenge.title}</h3>
                            <p>{challenge.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className='student-intro-section student-intro-how'>
                <h2 data-reveal>How does it work?</h2>
                <ol className='student-intro-steps'>
                    {STEPS.map((step, index) => (
                        <li
                            key={index}
                            className='student-intro-step'
                            style={{transitionDelay: `${index * 200}ms`}}
                            data-reveal
                        >
                            <div className='student-intro-step-number'>{index + 1}</div>
                            <div className='student-intro-step-icon' aria-hidden='true'>{step.icon}</div>
                            <h3>{step.title}</h3>
                            <p>{step.text}</p>
                        </li>
                    ))}
                </ol>
            </section>

            <section className='student-intro-section student-intro-skills'>
                <h2 data-reveal>Skills you’ll power up</h2>
                <p className='student-intro-lead' data-reveal>
                    Every challenge helps you level up skills you’ll use in school, at home and in your future job.
                </p>
                <div className='student-intro-badges'>
                    {SKILLS.map((skill, index) => (
                        <div
                            key={index}
                            className={`student-intro-badge badge-${index}`}
                            style={{transitionDelay: `${index * 120}ms`}}
                            data-reveal
                        >
                            <span className='student-intro-badge-icon' aria-hidden='true'>{skill.icon}</span>
                            <span>{skill.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className='student-intro-section student-intro-mindset'>
                <div className='student-intro-bubble' data-reveal>
                    <p className='student-intro-bubble-big'>Psst… here’s a secret.</p>
                    <p>
                        You don’t have to be a “math person” to be great at Mindsets.
                        Mistakes aren’t bad. They’re clues! Every try gets you closer, and your ideas really matter.
                    </p>
                </div>
            </section>

            <section className='student-intro-section student-intro-outro'>
                <Confetti trigger={celebrating}/>
                <h2 data-reveal>We’re really glad you’re here!</h2>
                <p className='student-intro-lead' data-reveal>
                    Your teacher picked Mindsets because they believe you can do amazing things. So do we.
                </p>
                <div className='student-intro-actions' data-reveal>
                    <button
                        className='student-intro-high-five'
                        onClick={() => setCelebrating(celebrating + 1)}
                    >
                        <span aria-hidden='true'>✋</span> Give us a high five!
                    </button>
                    <Link className='student-intro-login' to='/login'>
                        I’m ready, let’s go!
                    </Link>
                </div>
            </section>
        </div>
    );
}
